// Standalone bodyweight trend section (Summary view) — rendered below
// "Is the plan working?" (SummaryHero.js). A coach narrows the nutrition period
// to 1-2 weeks for normal check-ins, but reading whether bodyweight is
// trending the right way needs a much longer window to see past day-to-day
// noise (water/glycogen), so this card has its own range control, always
// anchored to real "today" and fully independent of whatever nutrition period
// is selected elsewhere.
//
// `dailyStats` must be the RAW, unfiltered list SummaryView.js already has —
// not the dayRows/series scoped to the selected nutrition period: the trend,
// the rate and the off-trend flags need weigh-ins from before the visible
// range (see utils/nutrition-progress/bodyweightTrend.js).

import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BODYWEIGHT_QUICK_RANGES,
  buildBodyweightTrend,
  DEFAULT_BODYWEIGHT_QUICK_RANGE,
  resolveQuickRange,
  toUtcNumber,
} from "../../../../../utils/nutrition-progress";
import { BodyweightTrendChart } from "./BodyweightTrendChart";
import { BodyweightTrendTiles } from "./BodyweightTrendTiles";

const bw = (t, key, opts) =>
  t(`nutritionPage.progress.bodyweightTrend.${key}`, opts);
// The buttons show short labels (1M ... All); the long ones ("1 Month",
// "Lifetime"...) are the nutritionPage.progress.<id> copy RangeControls.js
// already uses, kept as each button's accessible name.
const rangeLabel = (t, id) => t(`nutritionPage.progress.${id}`);

// "lifetime" is the client's whole weigh-in history: buildBodyweightTrend takes
// a null start for that, instead of a fixed window.
const rangeStartUtc = (quickRange) => {
  if (quickRange === "lifetime") return null;
  const { dateFrom } = resolveQuickRange(
    quickRange,
    new Date(),
    BODYWEIGHT_QUICK_RANGES,
    DEFAULT_BODYWEIGHT_QUICK_RANGE
  );
  return toUtcNumber(dateFrom);
};

export const BodyweightTrendCard = ({
  dailyStats = [],
  units,
  bodyweightGoal = null,
}) => {
  const { t } = useTranslation("athlete-portal");
  const [quickRange, setQuickRange] = useState(DEFAULT_BODYWEIGHT_QUICK_RANGE);
  // One "today" per mount, shared with every calculation (same reason as
  // SummaryView.js: two reads could land on opposite sides of midnight).
  const todayUtc = useMemo(() => toUtcNumber(new Date()), []);

  const trend = useMemo(
    () =>
      buildBodyweightTrend({
        dailyStats,
        fromUtc: rangeStartUtc(quickRange),
        todayUtc,
        bodyweightGoal,
      }),
    [dailyStats, quickRange, todayUtc, bodyweightGoal]
  );

  return (
    <div className="np-bwtrend">
      <div className="np-bwtrend__header">
        <div className="np-bwtrend__heading">
          <h3 className="np-bwtrend__title">{bw(t, "title")}</h3>
          <p className="np-bwtrend__subtitle">
            {bw(t, trend.goal ? "subtitleGoal" : "subtitle")}
          </p>
        </div>
        <div className="np-bwtrend__ranges">
          {BODYWEIGHT_QUICK_RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              className={quickRange === range.id ? "active" : undefined}
              aria-pressed={quickRange === range.id}
              aria-label={rangeLabel(t, range.id)}
              onClick={() => setQuickRange(range.id)}
            >
              {bw(t, `ranges.${range.id}`)}
            </button>
          ))}
        </div>
      </div>
      {trend.hasData ? (
        <>
          <BodyweightTrendTiles
            trend={trend}
            units={units}
            quickRange={quickRange}
          />
          <BodyweightTrendChart trend={trend} units={units} />
        </>
      ) : (
        <div className="np-bwtrend__empty">{bw(t, "noData")}</div>
      )}
    </div>
  );
};

BodyweightTrendCard.propTypes = {
  dailyStats: PropTypes.array,
  units: PropTypes.string,
  bodyweightGoal: PropTypes.object,
};
